import React, { useMemo, useState, useRef, useEffect } from "react";
import { Form } from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale/fr";
import { enUS } from "date-fns/locale/en-US";
import "react-datepicker/dist/react-datepicker.css";
import "./HistoricSidebar.css";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import FilterNoneOutlinedIcon from "@mui/icons-material/FilterNoneOutlined";
import CropSquareOutlinedIcon from "@mui/icons-material/CropSquareOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";
import { formatCountryLabel } from "../../../utils/countryLabel";
import DepartmentMultiSelect from "../../atoms/departmentMultiSelect/DepartmentMultiSelect";
import { AREA_RANGES } from "../../../constants/defaults";
import {
  toBackendDepartmentCode,
  fromBackendDepartmentCode,
} from "../../../utils/departmentCode";
import { useTranslation } from "react-i18next";
registerLocale("fr", fr);
registerLocale("en", enUS);

// Helpers to bridge react-datepicker's Date objects with the "YYYY-MM-DD"
// ISO strings the rest of this component (and its parent) work with.
const isoToDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);
const dateToIso = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Adjust MIN_YEAR to the earliest year covered by your historic fires data
const MIN_YEAR = 1900;
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - MIN_YEAR + 1 },
  (_, i) => CURRENT_YEAR - i, // newest first
);

const HistoricSidebar = ({
  onToggleHistoricSidebar,
  isOpenHistoricSidebar,
  onChangeHistoricFiresStartDate,
  onChangeHistoricFiresEndDate,
  historicFiresStartDate,
  historicFiresEndDate,
  showHistoricFiresList,
  showHistoricFiresByDateList,
  onShowHistoricFiresList,
  onShowHistoricFiresByDateList,
  formattedCurrentDate,
  historicFires,
  filteredHistoricFires,
  handleAddFireId,
  handleRemoveFireId,
  selectedFiresIds,
  onShowNavbarMenu,
  mapRef,
  reducedWindow,
  onReduceHistoricFiresnWindow,
  getHistoricFiresByDate,
  getHistoricFires,
  defaultHistoricEndDate,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith("fr") ? "fr" : "en";
  const monthNames = t("common.months", { returnObjects: true });
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { getAllowedCountries, getAllowedDepartments, getDateRangeLimits } =
    useAccessRights();
  const dateRangeLimits = useMemo(
    () => getDateRangeLimits("historic_fire_service"),
    [getDateRangeLimits],
  );

  const defaultEndDate = useMemo(() => {
    const raw = dateRangeLimits?.dateEnd;
    if (raw) {
      const d = new Date(raw);
      if (!isNaN(d)) return dateToIso(d); // -> "2024-08-31"
    }
    return formattedCurrentDate;
  }, [dateRangeLimits, formattedCurrentDate]);
  // track whether the current value is still "the auto default" so we don't
  // stomp on a value the user actually picked when defaultEndDate recomputes
  // (e.g. accessRights loading async)
  const prevDefaultEndDateRef = useRef(defaultEndDate);
  const defaultStartDate = useMemo(() => {
    const raw = dateRangeLimits?.dateStart;
    if (raw) {
      const d = new Date(raw);
      if (!isNaN(d)) return dateToIso(d);
    }
    return ""; // no lower bound -> nothing to send
  }, [dateRangeLimits]);

  // True only when the user has actually touched a date field, as opposed
  // to the end date just showing its auto-filled default.
  const hasExplicitDateFilter =
    !!historicFiresStartDate ||
    (!!historicFiresEndDate && historicFiresEndDate !== defaultEndDate);
  useEffect(() => {
    if (!historicFiresEndDate) {
      onChangeHistoricFiresEndDate(defaultEndDate);
      prevDefaultEndDateRef.current = defaultEndDate;
      return;
    }
    if (
      historicFiresEndDate === prevDefaultEndDateRef.current &&
      defaultEndDate !== prevDefaultEndDateRef.current
    ) {
      onChangeHistoricFiresEndDate(defaultEndDate);
    }
    prevDefaultEndDateRef.current = defaultEndDate;
  }, [defaultEndDate, historicFiresEndDate, onChangeHistoricFiresEndDate]);
  const minAllowedDate = useMemo(() => {
    if (!dateRangeLimits?.dateStart) return null;
    const d = new Date(dateRangeLimits.dateStart);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [dateRangeLimits]);

  const maxAllowedDate = useMemo(() => {
    if (!dateRangeLimits?.dateEnd) return null;
    const d = new Date(dateRangeLimits.dateEnd);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [dateRangeLimits]);

  const earlierDate = (a, b) => (!a ? b : !b ? a : a < b ? a : b);
  const laterDate = (a, b) => (!a ? b : !b ? a : a > b ? a : b);

  const startFieldMin = minAllowedDate || undefined;
  const startFieldMax =
    earlierDate(isoToDate(historicFiresEndDate), maxAllowedDate) || undefined;

  const endFieldMin =
    laterDate(isoToDate(historicFiresStartDate), minAllowedDate) || undefined;
  const endFieldMax = maxAllowedDate || undefined;

  // ---- Country / department permission lists ----
  const allowedCountries = useMemo(() => {
    const allowed = getAllowedCountries("historic_fire_service");
    return allowed
      .map((key) => ({ value: key, label: formatCountryLabel(key) }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [getAllowedCountries]);

  const allowedDepartments = useMemo(() => {
    const allowed = getAllowedDepartments("historic_fire_service");
    return allowed
      .map((key) => ({ value: key, label: fromBackendDepartmentCode(key) }))
      .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  }, [getAllowedDepartments]);

  // Determine if France is in the allowed countries
  const hasFrance = useMemo(() => {
    return allowedCountries.some((country) => country.value === "france");
  }, [allowedCountries]);

  // Compute default country once for initialization
  const defaultCountry = useMemo(() => {
    if (allowedCountries.length === 0) return "";
    if (hasFrance) return "france";
    return allowedCountries[0]?.value || "";
  }, [allowedCountries, hasFrance]);

  // Compute default departments once for initialization
  const defaultDepartments = useMemo(() => {
    if (defaultCountry === "france" && allowedDepartments.length > 0) {
      return allowedDepartments.map((dept) => dept.value);
    }
    return [];
  }, [defaultCountry, allowedDepartments]);

  // State for selected country and departments — use lazy initialization
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [selectedDepartments, setSelectedDepartments] =
    useState(defaultDepartments);
  const [selectedAreaRange, setSelectedAreaRange] = useState("");
  const selectedDepartmentCodes = useMemo(() => {
    if (selectedCountry !== "france") return null;
    return new Set(
      allowedDepartments
        .filter((d) => selectedDepartments.includes(d.value))
        .map((d) => toBackendDepartmentCode(d.label)),
    );
  }, [selectedDepartments, selectedCountry, allowedDepartments]);
  // Track previous country to only reset departments when user CHANGES country
  const prevCountryRef = useRef(defaultCountry);

  // Only reset departments when selectedCountry actually changes (user action)
  useEffect(() => {
    if (selectedCountry !== prevCountryRef.current) {
      prevCountryRef.current = selectedCountry;
      if (selectedCountry !== "france") {
        setSelectedDepartments([]);
      } else {
        if (allowedDepartments.length > 0) {
          setSelectedDepartments(allowedDepartments.map((dept) => dept.value));
        }
      }
    }
  }, [selectedCountry, allowedDepartments]);
  useEffect(() => {
    if (!selectedDepartmentCodes || selectedFiresIds.length === 0) return;

    const allKnownFires = [...historicFires, ...filteredHistoricFires];

    selectedFiresIds.forEach((id) => {
      const fire = allKnownFires.find((f) => f.id === id);
      if (!fire || !fire.department) return;

      if (
        !selectedDepartmentCodes.has(toBackendDepartmentCode(fire.department))
      ) {
        handleRemoveFireId(id);
      }
    });
  }, [selectedDepartmentCodes]);
  // Auto-deselect fires that no longer match the area filter
  useEffect(() => {
    if (!selectedAreaRange || selectedFiresIds.length === 0) return;

    const range = AREA_RANGES.find((r) => r.value === selectedAreaRange);
    if (!range) return;

    const allKnownFires = [...historicFires, ...filteredHistoricFires];

    selectedFiresIds.forEach((id) => {
      const fire = allKnownFires.find((f) => f.id === id);
      if (!fire) return;

      const areaHa = getItemAreaHa(fire);
      const stillMatches =
        areaHa !== null &&
        !isNaN(areaHa) &&
        areaHa >= range.min &&
        areaHa < range.max;

      if (!stillMatches) {
        handleRemoveFireId(id);
      }
    });
  }, [selectedAreaRange]);
  // Show department filter only when France is selected
  const showDepartmentFilter =
    selectedCountry === "france" && allowedDepartments.length > 0;

  // Handle start date change with auto-filter
  const handleStartDateChange = (e) => {
    const value = e.target.value;
    onChangeHistoricFiresStartDate(value);
    if (value && historicFiresEndDate) {
      setIsSearchClicked(true);
    }
  };

  const handleEndDateChange = (e) => {
    onChangeHistoricFiresEndDate(e.target.value);
    if (e.target.value && historicFiresStartDate) {
      setIsSearchClicked(true);
    }
  };
  // Adapters so DatePicker's onChange(Date) plugs into the existing
  // event-shaped handlers above without touching their internals.
  const handleStartDatePicked = (date) => {
    handleStartDateChange({ target: { value: dateToIso(date) } });
  };

  const handleEndDatePicked = (date) => {
    handleEndDateChange({ target: { value: dateToIso(date) } });
  };
  // CUSTOM DATEPICKER HEADER
  const makeCustomHeader =
    (onPick, minDate, maxDate) =>
    ({
      date,
      changeYear,
      changeMonth,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
    }) => {
      const commitDate = (updater) => {
        const base = date ? new Date(date) : new Date();
        updater(base);
        onPick(base);
      };

      const minYear = minDate ? minDate.getFullYear() : MIN_YEAR;
      const maxYear = maxDate ? maxDate.getFullYear() : CURRENT_YEAR;
      const currentYear = date ? date.getFullYear() : new Date().getFullYear();
      const currentMonth = date ? date.getMonth() : new Date().getMonth();

      const isMonthDisabled = (monthIndex) => {
        if (
          minDate &&
          currentYear === minDate.getFullYear() &&
          monthIndex < minDate.getMonth()
        ) {
          return true;
        }
        if (
          maxDate &&
          currentYear === maxDate.getFullYear() &&
          monthIndex > maxDate.getMonth()
        ) {
          return true;
        }
        return false;
      };

      return (
        <div className="datepicker-custom-header">
          <button
            type="button"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            className="datepicker-nav-btn"
          >
            {"<"}
          </button>

          <select
            value={currentMonth}
            onChange={({ target: { value } }) => {
              const month = Number(value);
              changeMonth(month);
              commitDate((d) => d.setMonth(month));
            }}
            className="datepicker-select"
          >
            {monthNames.map((month, i) => (
              <option key={month} value={i} disabled={isMonthDisabled(i)}>
                {month}
              </option>
            ))}
          </select>

          <select
            value={currentYear}
            onChange={({ target: { value } }) => {
              const year = Number(value);
              changeYear(year);
              commitDate((d) => {
                d.setFullYear(year);
                // clamp month if it falls outside the bound in the new year
                if (
                  minDate &&
                  year === minDate.getFullYear() &&
                  d.getMonth() < minDate.getMonth()
                ) {
                  d.setMonth(minDate.getMonth());
                }
                if (
                  maxDate &&
                  year === maxDate.getFullYear() &&
                  d.getMonth() > maxDate.getMonth()
                ) {
                  d.setMonth(maxDate.getMonth());
                }
              });
            }}
            className="datepicker-select"
          >
            {YEAR_OPTIONS.filter(
              (year) => year >= minYear && year <= maxYear,
            ).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            className="datepicker-nav-btn"
          >
            {">"}
          </button>
        </div>
      );
    };

  useEffect(() => {
    if (!isOpenHistoricSidebar) return;

    const effectiveStartDate = historicFiresStartDate || defaultStartDate;
    const effectiveEndDate = historicFiresEndDate || defaultEndDate;

    if (hasExplicitDateFilter) {
      onShowHistoricFiresByDateList();
      getHistoricFiresByDate(
        effectiveStartDate,
        effectiveEndDate,
        selectedCountry,
        selectedDepartments,
      );
    } else {
      if (!showHistoricFiresList) {
        onShowHistoricFiresList();
      }
      getHistoricFires(selectedCountry, selectedDepartments);
    }
  }, [
    isOpenHistoricSidebar,
    historicFiresStartDate,
    historicFiresEndDate,
    defaultStartDate,
    defaultEndDate,
    hasExplicitDateFilter,
    selectedCountry,
    selectedDepartments,
    showHistoricFiresList,
  ]);
  //the Annuler handler
  const openHistoricFiresList = (e) => {
    e.preventDefault();
    onShowHistoricFiresList();
    setIsSearchClicked(false);
    setSearchTerm("");
    onChangeHistoricFiresStartDate("");
    onChangeHistoricFiresEndDate(defaultEndDate);
    setSelectedCountry(defaultCountry);
    setSelectedDepartments(defaultDepartments);
    setSelectedAreaRange("");
    prevCountryRef.current = defaultCountry;
  };

  const returnToMenu = () => {
    onToggleHistoricSidebar();
    onShowNavbarMenu();
  };

  const performSearch = (term) => {
    setSearchTerm(term);
  };
  // Accepts either an area already in hectares, or area_m2 (converted).
  const getItemAreaHa = (item) => {
    if (
      item.area_ha !== undefined &&
      item.area_ha !== null &&
      item.area_ha !== ""
    ) {
      return parseFloat(item.area_ha);
    }
    if (
      item.area_m2 !== undefined &&
      item.area_m2 !== null &&
      item.area_m2 !== ""
    ) {
      return parseFloat(item.area_m2) / 10000;
    }
    return null;
  };
  const getDisplayData = () => {
    let baseData = [];
    if (showHistoricFiresByDateList) {
      baseData = filteredHistoricFires;
    } else if (showHistoricFiresList) {
      baseData = historicFires;
    }

    // Filter by search term
    let filteredData = baseData;
    if (searchTerm && searchTerm.length >= 2) {
      const searchTermLower = searchTerm.toLowerCase();
      filteredData = baseData.filter((item) => {
        const searchableFields = [
          item.label?.toLowerCase() || "",
          item.commune?.toLowerCase() || "",
          item.department?.toLowerCase() || "",
          item.alerte?.toLowerCase() || "",
          item.end_date?.toLowerCase() || "",
          item.date?.toLowerCase() || "",
          item.nature?.toLowerCase() || "",
          item.year?.toString() || "",
        ];
        return searchableFields.some((field) =>
          field.includes(searchTermLower),
        );
      });
    }
    if (selectedAreaRange) {
      const range = AREA_RANGES.find((r) => r.value === selectedAreaRange);
      if (range) {
        filteredData = filteredData.filter((item) => {
          const areaHa = getItemAreaHa(item);
          if (areaHa === null || isNaN(areaHa)) return false;
          return areaHa >= range.min && areaHa < range.max;
        });
      }
    }
    return filteredData;
  };

  const deselectAllDisplayedFires = (e) => {
    e.preventDefault();
    const displayedData = getDisplayData();
    displayedData.forEach((item) => {
      const idStr = String(item.id);
      if (selectedFiresIds.includes(idStr)) {
        handleRemoveFireId(idStr);
      }
    });
  };

  const isDepartmentsFilterActive = useMemo(() => {
    if (selectedDepartments.length !== defaultDepartments.length) return true;
    const defaultSet = new Set(defaultDepartments);
    return selectedDepartments.some((d) => !defaultSet.has(d));
  }, [selectedDepartments, defaultDepartments]);

  const isFilterActive =
    hasExplicitDateFilter ||
    !!searchTerm ||
    selectedCountry !== defaultCountry ||
    isDepartmentsFilterActive;

  const displayData = getDisplayData();

  const hasSelectedDisplayedFires = displayData.some((item) =>
    selectedFiresIds.includes(String(item.id)),
  );

  // Handle department change
  const handleDepartmentChange = (newDepartments) => {
    setSelectedDepartments(newDepartments);
  };

  return (
    <div className={`sidebar ${isOpenHistoricSidebar ? "open" : ""}`}>
      <div className="drag-comp-header">
        <KeyboardDoubleArrowLeftIcon
          onClick={returnToMenu}
          className="drag-comp-returnIcon"
        />

        <div className="drag-comp-title">{t("historicSidebar.title")}</div>
        <div className="drag-header-icons">
          {reducedWindow ? (
            <CropSquareOutlinedIcon
              onClick={onReduceHistoricFiresnWindow}
              className="drag-comp-closeIcon"
              style={{ fontSize: "20px" }}
            />
          ) : (
            <FilterNoneOutlinedIcon
              onClick={onReduceHistoricFiresnWindow}
              className="drag-comp-closeIcon"
              style={{ fontSize: "17px" }}
            />
          )}
          <CloseIcon
            onClick={onToggleHistoricSidebar}
            className="drag-comp-closeIcon"
          />
        </div>
      </div>

      {!reducedWindow && (
        <div>
          <div className="historic-sidebar-section" style={{ padding: "10px" }}>
            {searchTerm && searchTerm.length < 2 && (
              <small
                style={{
                  color: "#999",
                  marginTop: "5px",
                  display: "block",
                  paddingLeft: "10px",
                }}
              >
                {t("historicSidebar.searchMinChars")}
              </small>
            )}
            {searchTerm &&
              searchTerm.length >= 2 &&
              getDisplayData().length === 0 && (
                <small
                  style={{
                    color: "#b10511",
                    marginTop: "5px",
                    display: "block",
                    paddingLeft: "10px",
                  }}
                >
                  {t("historicSidebar.noResultsFor", { term: searchTerm })}
                </small>
              )}
          </div>

          <div className="historic-sidebar-section">
            <Form style={{ padding: "0px 10px" }} autoComplete="off">
              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom">
                  {t("historicSidebar.search.label")}
                </Form.Label>
                <div className="search-input-container">
                  <SearchIcon className="search-icon" />
                  <input
                    type="text"
                    placeholder={t("historicSidebar.search.placeholder")}
                    value={searchTerm}
                    onChange={(e) => performSearch(e.target.value)}
                    className="form-control-own search-input"
                    data-testid="historical-fires-search-input"
                  />
                </div>
              </Form.Group>
              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom">
                  {t("historicSidebar.areaFilter.label")}
                </Form.Label>
                <select
                  value={selectedAreaRange}
                  onChange={(e) => setSelectedAreaRange(e.target.value)}
                  className="form-control-own"
                  data-testid="historical-fires-area-select"
                >
                  <option value="">
                    {t("historicSidebar.areaFilter.allAreas")}
                  </option>
                  {AREA_RANGES.map((range) => (
                    <option key={range.value} value={range.value}>
                      {range.label}
                    </option>
                  ))}
                </select>
              </Form.Group>
              {/* Country filter */}
              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom">
                  {t("historicSidebar.country.label")}
                </Form.Label>
                <select
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className="form-control-own"
                  data-testid="historical-fires-country-select"
                >
                  {allowedCountries.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </Form.Group>

              {/* Department filter only when France is selected */}
              {showDepartmentFilter && (
                <Form.Group className="form-group-custom">
                  <Form.Label className="form-label-custom">
                    {t("historicSidebar.department.label")}
                  </Form.Label>
                  <DepartmentMultiSelect
                    options={allowedDepartments}
                    selectedValues={selectedDepartments}
                    onChange={handleDepartmentChange}
                    disabled={false}
                    isSmallScreen={false}
                    placeholder={t("historicSidebar.department.placeholder")}
                    maxCustom={allowedDepartments.length}
                    allowSelectAllControls
                  />
                  {selectedDepartments.length === 1 && (
                    <small
                      style={{
                        color: "#999",
                        fontSize: "11px",
                        display: "block",
                        marginTop: "4px",
                      }}
                    >
                      {t("historicSidebar.department.minOneRequired")}
                    </small>
                  )}
                </Form.Group>
              )}

              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom">
                  {t("historicSidebar.dateFrom.label")}
                </Form.Label>
                <DatePicker
                  selected={isoToDate(historicFiresStartDate)}
                  onChange={handleStartDatePicked}
                  locale={dateLocale}
                  dateFormat={dateLocale === "fr" ? "dd/MM/yyyy" : "MM/dd/yyyy"}
                  placeholderText={t("historicSidebar.dateFrom.placeholder")}
                  className="form-control-own"
                  wrapperClassName="w-100"
                  minDate={startFieldMin}
                  maxDate={startFieldMax}
                  isClearable
                  renderCustomHeader={makeCustomHeader(
                    handleStartDatePicked,
                    startFieldMin,
                    startFieldMax,
                  )}
                  data-testid="historical-fires-date-from-input"
                />
              </Form.Group>

              <Form.Group className="form-group-custom">
                <Form.Label className="form-label-custom">
                  {t("historicSidebar.dateTo.label")}
                </Form.Label>
                <DatePicker
                  selected={isoToDate(historicFiresEndDate)}
                  onChange={handleEndDatePicked}
                  locale={dateLocale}
                  dateFormat={dateLocale === "fr" ? "dd/MM/yyyy" : "MM/dd/yyyy"}
                  placeholderText={t("historicSidebar.dateTo.placeholder")}
                  className="form-control-own"
                  wrapperClassName="w-100"
                  minDate={endFieldMin}
                  maxDate={endFieldMax}
                  isClearable
                  renderCustomHeader={makeCustomHeader(
                    handleEndDatePicked,
                    endFieldMin,
                    endFieldMax,
                  )}
                  data-testid="historical-fires-date-to-input"
                />
              </Form.Group>

              <div className="historic-filter-buttons">
                <button
                  className="historic-secondary-btn"
                  onClick={openHistoricFiresList}
                  disabled={!isFilterActive}
                  style={{
                    opacity: !isFilterActive ? 0.4 : 1,
                    cursor: !isFilterActive ? "not-allowed" : "pointer",
                  }}
                  data-testid="historical-fires-cancel-button"
                >
                  {t("historicSidebar.buttons.cancel")}
                </button>

                <button
                  className="historic-primary-btn"
                  onClick={deselectAllDisplayedFires}
                  disabled={!hasSelectedDisplayedFires}
                  style={{
                    opacity: !hasSelectedDisplayedFires ? 0.4 : 1,
                    cursor: !hasSelectedDisplayedFires
                      ? "not-allowed"
                      : "pointer",
                  }}
                  data-testid="historical-fires-deselect-all-button"
                >
                  {t("historicSidebar.buttons.deselectAll")}
                </button>
              </div>
            </Form>
          </div>

          <div style={{ padding: "5px 15px", fontSize: "12px", color: "#666" }}>
            {t("historicSidebar.resultsCount", { count: displayData.length })}
          </div>

          <div
            className="historic-fires-list drag-comp-body"
            data-testid="historical-fires-list"
          >
            {displayData.map((item, index) => {
              const idStr = String(
                item.id ?? `${item.commune}-${item.alerte}-${index}`,
              );
              return (
                <div className="historic-fires-item" key={item.id || index}>
                  <div>
                    <h6>{item.label}</h6>
                    <p>
                      {fromBackendDepartmentCode(item.department)} /{" "}
                      {item.country
                        ? item.country.charAt(0).toUpperCase() +
                          item.country.slice(1)
                        : item.country}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedFiresIds.includes(idStr)}
                    onChange={(e) =>
                      e.target.checked
                        ? handleAddFireId(idStr)
                        : handleRemoveFireId(idStr)
                    }
                    data-testid="historical-simulations-checkbox"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(HistoricSidebar);
